import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { sanitizeQueryParam } from '@v2/lib';
import toast from 'react-hot-toast';
import '@v2/styles/tailwind.css';
import {
  BitwerxOfferingsTable,
  BitwerxOfferingPluginManagementPanel,
  BitwerxOfferingSubOfferingPanel,
} from './components';
import { BitwerxOffering, BitwerxSubOfferingDetail, OfferingPlugin, UpdateBitwerxOfferingPayload } from '@v2/types';
import { usePermissionsContext, Permissions } from '@v2/contexts';
import {
  useBitwerxOfferings,
  useBitwerxOfferingDetails,
  useBitwerxOfferingPlugins,
  useRemovePluginFromOffering,
  useRemoveBitwerxSubOffering,
  useRemoveBitwerxOffering,
  useUpdateBitwerxOffering,
  useTableFilters,
} from '@v2/hooks';
import { Button, Input, Modal } from '@v2/components';
import type { ActionMenuItem } from '@v2/components';
import { SettingsPageLayout, DetailPanel, ListDetailView } from '@v2/layouts';
import { ManageOfferingPlugins, ManageOfferingsForm } from '@v2/features';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLaptop, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';

const BitwerxOfferingsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const selectedOfferingId = sanitizeQueryParam(searchParams.get('offeringId'));

  const { hasPermission } = usePermissionsContext();
  const canSeePluginManagement = hasPermission([Permissions.BitwerxOfferingPluginManagerView]);
  const canAddOrRemovePlugin = hasPermission([Permissions.BitwerxOfferingPluginManagerEdit]);
  const canModifyOfferings = hasPermission(Permissions.ModifyBitwerxOfferings);

  const {
    offerings,
    loading: offeringsLoading,
    error: offeringsError,
    refetch: refetchOfferings,
  } = useBitwerxOfferings();

  const {
    filteredData,
    search,
    setSearch,
    sortConfig,
    handleSort,
  } = useTableFilters({
    data: offerings,
    searchKeys: ['name'],
    initialSort: { key: 'name', direction: 'asc' },
  });

  const {
    offering,
    loading: detailsLoading,
    error: detailsError,
    refetch: refetchDetails,
  } = useBitwerxOfferingDetails(selectedOfferingId);

  const {
    plugins,
    loading: pluginsLoading,
    error: pluginsError,
    refetch: refetchPlugins,
  } = useBitwerxOfferingPlugins(offering?.name ?? null);

  const { removePluginFromOffering } = useRemovePluginFromOffering();
  const { removeBitwerxSubOffering } = useRemoveBitwerxSubOffering();
  const { deleteBitwerxOffering, loading: deleteOfferingLoading } = useRemoveBitwerxOffering();
  const { updateBitwerxOffering, loading: updateOfferingLoading } = useUpdateBitwerxOffering();

  const [isEditNameOpen, setIsEditNameOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const handleStartEditName = () => {
    setEditName(offering?.name ?? '');
    setIsEditNameOpen(true);
  };

  const handleSaveEditName = async () => {
    if (!offering || !editName.trim() || editName.trim() === offering.name) {
      setIsEditNameOpen(false);
      setEditName('');
      return;
    }
    const trimmedName = editName.trim();
    const payload: UpdateBitwerxOfferingPayload = {
      id: offering.id,
      displayId: 0,
      name: trimmedName,
      newName: trimmedName,
      subOfferings: (offering.subOfferings ?? []).map((sub) => ({
        name: sub.name,
        displayName: sub.displayName ?? sub.name,
        addition: false,
      })),
      createdById: offering.createdById,
      createdDate: offering.createdDate,
      createdByFirstName: offering.createdByFirstName,
      createdByLastName: offering.createdByLastName,
      modifiedById: offering.modifiedById,
      modifiedDate: offering.modifiedDate,
    };
    const result = await updateBitwerxOffering(offering.id, payload);
    if (result !== null) {
      toast.success('Offering name updated');
      setIsEditNameOpen(false);
      setEditName('');
      refetchDetails();
      refetchOfferings();
    } else {
      toast.error('Failed to update offering name');
    }
  };

  const handleConfirmDeleteOffering = async () => {
    if (!offering) return;
    const result = await deleteBitwerxOffering(offering.id);
    if (result !== null) {
      toast.success('Offering deleted');
      setIsConfirmDeleteOpen(false);
      refetchOfferings();
      searchParams.delete('offeringId');
      history.push({ search: searchParams.toString() });
    } else {
      toast.error('Failed to delete offering');
    }
  };

  const handleSelectOffering = (selected: BitwerxOffering) => {
    searchParams.set('offeringId', selected.id);
    history.push({ search: searchParams.toString() });
  };

  const handleClosePanel = () => {
    searchParams.delete('offeringId');
    history.push({ search: searchParams.toString() });
  };

  const [isManagePluginsModalOpen, setIsManagePluginsModalOpen] = useState(false);
  const [isCreateOfferingOpen, setIsCreateOfferingOpen] = useState(false);
  const [isAddSubOfferingOpen, setIsAddSubOfferingOpen] = useState(false);
  const [addEntityName, setAddEntityName] = useState('');
  const [addDisplayName, setAddDisplayName] = useState('');

  const handleAddSubOffering = () => {
    setAddEntityName('');
    setAddDisplayName('');
    setIsAddSubOfferingOpen(true);
  };

  const handleSubmitAddSubOffering = async () => {
    if (!offering || !addEntityName.trim()) return;
    const payload: UpdateBitwerxOfferingPayload = {
      id: offering.id,
      displayId: 0,
      name: '',
      subOfferings: [
        ...(offering.subOfferings ?? []).map((sub) => ({
          name: sub.name,
          displayName: sub.displayName ?? sub.name,
          addition: false,
        })),
        {
          name: addEntityName.trim(),
          displayName: addDisplayName.trim() || addEntityName.trim(),
          addition: true,
        },
      ],
      createdById: offering.createdById,
      createdDate: offering.createdDate,
      createdByFirstName: offering.createdByFirstName,
      createdByLastName: offering.createdByLastName,
      modifiedById: offering.modifiedById,
      modifiedDate: offering.modifiedDate,
    };
    const result = await updateBitwerxOffering(offering.id, payload);
    if (result !== null) {
      toast.success('Sub offering added');
      setIsAddSubOfferingOpen(false);
      setAddEntityName('');
      setAddDisplayName('');
      refetchDetails();
      refetchOfferings();
    } else {
      toast.error('Failed to add sub offering');
    }
  };

  const handleDeleteSubOffering = async (subOffering: BitwerxSubOfferingDetail) => {
    const result = await removeBitwerxSubOffering(subOffering.id);
    if (result !== null) {
      toast.success('Sub offering removed');
      refetchDetails();
      refetchOfferings();
    } else {
      toast.error('Failed to remove sub offering');
    }
  };

  const handleCreateOffering = () => {
    setIsCreateOfferingOpen(true);
  };

  const handleAddPlugin = () => {
    setIsManagePluginsModalOpen(true);
  };

  const handleDeletePlugin = async (plugin: OfferingPlugin) => {
    if (!offering?.name?.trim()) return;
    const response = await removePluginFromOffering(offering.name.trim(), plugin.id);
    if (response !== null && response.isCompletedSuccessfully) {
      toast.success('Plugin removed from offering');
      await refetchPlugins();
    } else {
      toast.error('Failed to remove plugin from offering');
    }
  };

  const onCompletedDeleteOffering = () => {
    refetchOfferings();
    setIsCreateOfferingOpen(false);
    searchParams.delete('offeringId');
    history.push({ search: searchParams.toString() });
  };

  const onCompletedCreateOffering = (offeringId: string) => {
    refetchOfferings();
    refetchDetails();
    searchParams.set('offeringId', offeringId);
    history.push({ search: searchParams.toString() });
    setIsCreateOfferingOpen(false);
  };

  return (
    <SettingsPageLayout
      icon={faLaptop}
      title="Bitwerx Offerings"
      actionLabel={canModifyOfferings ? 'Create offering' : undefined}
      onAction={handleCreateOffering}
    >
      <ListDetailView
        selectedId={selectedOfferingId}
        list={
          <BitwerxOfferingsTable
            offerings={filteredData}
            loading={offeringsLoading}
            error={offeringsError}
            onSelectOffering={handleSelectOffering}
            selectedOfferingId={selectedOfferingId || undefined}
            sortConfig={sortConfig}
            onSortChange={handleSort}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name..."
          />
        }
        detail={
          <DetailPanel
            title={offering?.name || 'Offerings Details'}
            onClose={handleClosePanel}
            actions={
              canModifyOfferings && offering
                ? ([
                    {
                      key: 'edit',
                      label: 'Edit name',
                      icon: <FontAwesomeIcon icon={faPen} />,
                      onClick: handleStartEditName,
                    },
                    {
                      key: 'delete',
                      label: 'Delete offering',
                      icon: <FontAwesomeIcon icon={faTrash} />,
                      variant: 'danger' as const,
                      onClick: () => setIsConfirmDeleteOpen(true),
                    },
                  ] as ActionMenuItem[])
                : undefined
            }
            loading={detailsLoading}
            loadingMessage="Loading offerings details..."
            error={detailsError}
            errorMessage="There was an error fetching the offerings details."
            onRetry={refetchDetails}
          >
            {offering && (
              <div className="tw-flex tw-flex-col tw-gap-4">
                {canSeePluginManagement && (
                  <BitwerxOfferingPluginManagementPanel
                    offering={offering}
                    plugins={plugins}
                    pluginsLoading={pluginsLoading}
                    pluginsError={pluginsError}
                    onRetryPlugins={() => refetchPlugins()}
                    onAddPlugin={canAddOrRemovePlugin ? handleAddPlugin : undefined}
                    onDeletePlugin={canAddOrRemovePlugin ? handleDeletePlugin : undefined}
                  />
                )}
                <BitwerxOfferingSubOfferingPanel
                  offering={offering}
                  onAddSubOffering={canModifyOfferings ? handleAddSubOffering : undefined}
                  onDeleteSubOffering={canModifyOfferings ? handleDeleteSubOffering : undefined}
                  canModify={canModifyOfferings}
                />
              </div>
            )}
          </DetailPanel>
        }
      />
      {isManagePluginsModalOpen && (
        <ManageOfferingPlugins
          offeringName={offering?.name ?? null}
          onClose={() => setIsManagePluginsModalOpen(false)}
          onCompleted={() => {
            setIsManagePluginsModalOpen(false);
            refetchPlugins();
          }}
        />
      )}
      {isCreateOfferingOpen && (
        <ManageOfferingsForm
          offering={null}
          onClose={() => setIsCreateOfferingOpen(false)}
          onCompleted={() => {
            refetchDetails();
            refetchOfferings();
          }}
          onCompletedDeleteOffering={onCompletedDeleteOffering}
          onCompletedCreateOffering={onCompletedCreateOffering}
        />
      )}
      {isAddSubOfferingOpen && offering && (
        <Modal
          isOpen={true}
          onClose={() => setIsAddSubOfferingOpen(false)}
          title="Add Sub Offering"
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setIsAddSubOfferingOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmitAddSubOffering}
                disabled={!addEntityName.trim() || updateOfferingLoading}
                isLoading={updateOfferingLoading}
              >
                Add
              </Button>
            </>
          }
        >
          <div className="tw-flex tw-flex-col tw-gap-4">
            <Input
              label="Entity Name"
              value={addEntityName}
              onChange={(e) => setAddEntityName(e.target.value)}
              placeholder="Entity name"
              autoComplete="off"
            />
            <Input
              label="Display Name"
              value={addDisplayName}
              onChange={(e) => setAddDisplayName(e.target.value)}
              placeholder="Display name"
              autoComplete="off"
            />
          </div>
        </Modal>
      )}
      {isEditNameOpen && offering && (
        <Modal
          isOpen={true}
          onClose={() => setIsEditNameOpen(false)}
          title="Edit Offering Name"
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setIsEditNameOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveEditName}
                disabled={!editName.trim() || editName.trim() === offering.name || updateOfferingLoading}
                isLoading={updateOfferingLoading}
              >
                Save
              </Button>
            </>
          }
        >
          <Input
            label="Offering Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveEditName();
            }}
            placeholder="Offering name"
            autoComplete="off"
          />
        </Modal>
      )}
      {isConfirmDeleteOpen && offering && (
        <Modal
          isOpen={true}
          onClose={() => setIsConfirmDeleteOpen(false)}
          title="Delete Bitwerx Offering"
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setIsConfirmDeleteOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmDeleteOffering}
                disabled={deleteOfferingLoading}
                isLoading={deleteOfferingLoading}
              >
                Delete
              </Button>
            </>
          }
        >
          <p className="tw-text-sm tw-text-neutral-700">
            Are you sure you want to delete <strong>{offering.name}</strong>?
          </p>
        </Modal>
      )}
    </SettingsPageLayout>
  );
};

export default BitwerxOfferingsPage;
